import { DashboardWidgetInstance, DASHBOARD_GRID_COLUMNS } from "models/Dashboard";
import type { Layout, LayoutItem } from "react-grid-layout";

export type DashboardColumnCount = 2 | 3 | 4;

export interface DashboardWidgetConstraint {
    minimumWidth: (columns: DashboardColumnCount) => number;
    minimumHeight: number;
}

export const dashboardColumnsForWidth = (width: number): DashboardColumnCount => {
    if (width >= 992) return 4;
    if (width >= 576) return 3;
    return 2;
};

const collides = (left: LayoutItem, right: LayoutItem) =>
    left.x < right.x + right.w
    && left.x + left.w > right.x
    && left.y < right.y + right.h
    && left.y + left.h > right.y;

const candidateColumns = (desired: number, width: number, columns: number) =>
    Array.from({ length: columns - width + 1 }, (_, index) => index)
        .sort((left, right) => Math.abs(left - desired) - Math.abs(right - desired) || left - right);

export const projectDashboardLayout = (
    widgets: DashboardWidgetInstance[],
    columns: DashboardColumnCount,
    constraintFor: (widget: DashboardWidgetInstance) => DashboardWidgetConstraint,
): Layout => {
    const unit = DASHBOARD_GRID_COLUMNS / columns;
    const placed: LayoutItem[] = [];
    const sorted = widgets.slice().sort((left, right) =>
        left.position.y - right.position.y
        || left.position.x - right.position.x
        || left.id.localeCompare(right.id));

    sorted.forEach(widget => {
        const constraint = constraintFor(widget);
        const width = Math.min(columns, Math.max(
            constraint.minimumWidth(columns),
            Math.round(widget.position.width / unit),
        ));
        const desiredX = Math.min(columns - width, Math.max(0, Math.round(widget.position.x / unit)));
        const height = Math.max(constraint.minimumHeight, widget.position.height);
        const columnsToTry = candidateColumns(desiredX, width, columns);
        let item: LayoutItem | null = null;

        for (let y = 0; item === null; y += 1) {
            for (const x of columnsToTry) {
                const candidate: LayoutItem = {
                    i: widget.id,
                    x,
                    y,
                    w: width,
                    h: height,
                    minW: constraint.minimumWidth(columns),
                    minH: constraint.minimumHeight,
                    maxW: columns,
                };
                if (!placed.some(existing => collides(existing, candidate))) {
                    item = candidate;
                    break;
                }
            }
        }
        placed.push(item);
    });

    return placed;
};

export const applyVisibleLayout = (
    widgets: DashboardWidgetInstance[],
    layout: Layout,
    columns: DashboardColumnCount,
    resizedWidgetID?: string,
): DashboardWidgetInstance[] => {
    const unit = DASHBOARD_GRID_COLUMNS / columns;
    const byID = new Map(layout.map(item => [item.i, item]));
    const positioned = widgets.map(widget => {
        const item = byID.get(widget.id);
        const width = widget.id === resizedWidgetID && item ? item.w * unit : widget.position.width;
        return {
            ...widget,
            position: {
                x: item ? Math.min(item.x * unit, DASHBOARD_GRID_COLUMNS - width) : widget.position.x,
                y: item?.y ?? widget.position.y,
                width,
                height: widget.id === resizedWidgetID && item ? item.h : widget.position.height,
            },
        };
    });

    return normalizeDashboardPositions(positioned);
};

export const normalizeDashboardPositions = (widgets: DashboardWidgetInstance[]): DashboardWidgetInstance[] => {
    const placed: LayoutItem[] = [];
    const normalized = new Map<string, DashboardWidgetInstance>();
    widgets
        .slice()
        .sort((left, right) => left.position.y - right.position.y || left.position.x - right.position.x || left.id.localeCompare(right.id))
        .forEach(widget => {
            const width = Math.min(DASHBOARD_GRID_COLUMNS, Math.max(1, widget.position.width));
            const height = Math.max(1, widget.position.height);
            const desiredX = Math.min(DASHBOARD_GRID_COLUMNS - width, Math.max(0, widget.position.x));
            const columnsToTry = candidateColumns(desiredX, width, DASHBOARD_GRID_COLUMNS);
            let item: LayoutItem | null = null;
            for (let y = Math.max(0, widget.position.y); item === null; y += 1) {
                for (const x of columnsToTry) {
                    const candidate = { i: widget.id, x, y, w: width, h: height };
                    if (!placed.some(existing => collides(existing, candidate))) {
                        item = candidate;
                        break;
                    }
                }
            }
            placed.push(item);
            normalized.set(widget.id, { ...widget, position: { x: item.x, y: item.y, width: item.w, height: item.h } });
        });

    return widgets.map(widget => normalized.get(widget.id) ?? widget);
};

export const nextDashboardPosition = (
    widgets: DashboardWidgetInstance[],
    width: number,
    height: number,
) => ({
    x: 0,
    y: widgets.reduce((bottom, widget) => Math.max(bottom, widget.position.y + widget.position.height), 0),
    width,
    height,
});
